import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'
export type Density = 'comfortable' | 'compact'
export type DateFormat = 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd'
export type DisplayCurrency = 'XOF' | 'USD' | 'EUR'

export interface NotificationPrefs {
  quote_accepted: boolean
  quote_rejected: boolean
  quote_expiring: boolean
}

interface UIState {
  theme: Theme
  density: Density
  sidebarCollapsed: boolean
  mobileSidebarOpen: boolean
  sidebarSectionsCollapsed: Record<string, boolean>
  timezone: string
  dateFormat: DateFormat
  defaultCurrency: DisplayCurrency
  usdRate: number
  eurRate: number
  notifications: NotificationPrefs

  setTheme: (theme: Theme) => void
  setDensity: (density: Density) => void
  toggleDensity: () => void
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  openMobileSidebar: () => void
  closeMobileSidebar: () => void
  toggleMobileSidebar: () => void
  toggleSidebarSection: (key: string) => void
  setTimezone: (tz: string) => void
  setDateFormat: (format: DateFormat) => void
  setDefaultCurrency: (c: DisplayCurrency) => void
  setUsdRate: (rate: number) => void
  setEurRate: (rate: number) => void
  setNotification: (key: keyof NotificationPrefs, enabled: boolean) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'system',
      density: 'comfortable',
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      sidebarSectionsCollapsed: {},
      timezone: 'Africa/Dakar',
      dateFormat: 'dd/MM/yyyy',
      defaultCurrency: 'XOF',
      usdRate: 600,
      eurRate: 655.957,
      notifications: {
        quote_accepted: true,
        quote_rejected: true,
        quote_expiring: true,
      },

      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      toggleDensity: () =>
        set((s) => ({ density: s.density === 'compact' ? 'comfortable' : 'compact' })),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      openMobileSidebar: () => set({ mobileSidebarOpen: true }),
      closeMobileSidebar: () => set({ mobileSidebarOpen: false }),
      toggleMobileSidebar: () => set((s) => ({ mobileSidebarOpen: !s.mobileSidebarOpen })),
      toggleSidebarSection: (key) =>
        set((s) => ({
          sidebarSectionsCollapsed: {
            ...s.sidebarSectionsCollapsed,
            [key]: !s.sidebarSectionsCollapsed[key],
          },
        })),
      setTimezone: (timezone) => set({ timezone }),
      setDateFormat: (dateFormat) => set({ dateFormat }),
      setDefaultCurrency: (defaultCurrency) => set({ defaultCurrency }),
      setUsdRate: (usdRate) => set({ usdRate: usdRate > 0 ? usdRate : 1 }),
      setEurRate: (eurRate) => set({ eurRate: eurRate > 0 ? eurRate : 1 }),
      setNotification: (key, enabled) =>
        set((s) => ({ notifications: { ...s.notifications, [key]: enabled } })),
    }),
    {
      name: 'amanet-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        density: state.density,
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarSectionsCollapsed: state.sidebarSectionsCollapsed,
        timezone: state.timezone,
        dateFormat: state.dateFormat,
        defaultCurrency: state.defaultCurrency,
        usdRate: state.usdRate,
        eurRate: state.eurRate,
        notifications: state.notifications,
      }),
    },
  ),
)
